import { PILLAR_MAX, verdictKey } from "./reportUtils";

// Pure model for the summary report v2 blocks. Like pickTopHooks these return
// descriptors ({id, args}), never text or JSX: SummaryReport looks the wording
// up in summaryCopy.js by `id`. Every value comes from data the free report
// already carries; when a datum is missing the entry is simply omitted — no
// undefined, no NaN, no invented numbers.

const isNum = (v) => typeof v === "number" && Number.isFinite(v);
const count = (v) => (isNum(v) ? v : 0);

// One entry per pillar, PILLAR_MAX order. Without score_pillars there is
// nothing verifiable to show, so the whole block is skipped.
export function pillarOverview(data) {
  const pillars = data?.score_pillars;
  if (!pillars || typeof pillars !== "object") return [];
  const teaser = Array.isArray(data.audit_findings_teaser) ? data.audit_findings_teaser : [];
  const sampleCode = data.sample_finding?.code;
  return Object.keys(PILLAR_MAX).map((key) => {
    const max = PILLAR_MAX[key];
    const score = isNum(pillars[key]) ? pillars[key] : 0;
    // The sample finding is shown openly, so it does not count as locked.
    // no_website is shown openly (the owner knows it), so it is not "locked" either.
    const lockedCount = teaser.filter(
      (f) => f && f.pillar === key && f.code !== "no_website" && (sampleCode == null || f.code !== sampleCode)
    ).length;
    // locked > sample > complete > open. "sample" = nothing locked, but the
    // openly shown sample finding sits in this area, so a point is open.
    // Scores are rounded server-side, so a full score can still carry an open
    // finding: "complete" only when no finding of this area is visible at all.
    const sampleHere = data.sample_finding?.pillar === key;
    const state =
      lockedCount > 0 ? "locked"
        : sampleHere ? "sample"
          : score >= max ? "complete"
            : "open";
    const row = { key, score, max, pct: Math.round((score / max) * 100), lockedCount, state };
    // Only the gbp pillar has a manual-review coverage counter (see
    // audit/scoring.py gbp_manual_progress) — an honest "N of 24 points
    // manually reviewed" note, independent of the locked/sample/complete/open
    // state above, so an unreviewed pillar doesn't show a crushed score with
    // no explanation.
    if (key === "gbp") {
      const mp = data.gbp_manual_progress;
      if (mp && isNum(mp.reviewed) && isNum(mp.total) && mp.total > 0) {
        row.manualProgress = { reviewed: mp.reviewed, total: mp.total };
      }
    }
    return row;
  });
}

// Per-pillar average of the competitors' pillar scores (the radar's dashed
// benchmark). Only values that are present count; null when no competitor
// carries pillar data at all.
export function benchmarkPillars(top3, pillarKeys) {
  const list = Array.isArray(top3) ? top3 : [];
  if (!list.some((t) => t && t.pillars)) return null;
  return pillarKeys.reduce((acc, key) => {
    const vals = list.map((t) => t?.pillars?.[key]).filter((v) => v != null);
    if (vals.length) acc[key] = vals.reduce((a, b) => a + b, 0) / vals.length;
    return acc;
  }, {});
}

export function kpiChips(data) {
  const d = data || {};
  const chips = [];

  const fc = d.finding_counts;
  if (fc && fc.total > 0) {
    chips.push({ id: "findings", args: [fc.total, count(fc.critical), count(fc.high), count(fc.medium), count(fc.low)] });
  }

  const legalN = d.legal_teaser?.issue_count;
  if (isNum(legalN) && legalN > 0) chips.push({ id: "legal", args: [legalN] });

  const top1 = Array.isArray(d.top3) ? d.top3[0] : null;
  if (top1 && isNum(top1.score) && isNum(d.digital_score) && top1.label) {
    const gap = top1.score - d.digital_score;
    // Only when the competitor is actually ahead; otherwise it is no shortcoming.
    if (gap > 0) chips.push({ id: "gap", args: [gap, top1.label] });
  }

  const mr = d.maps_rank;
  if (mr && !mr.error && mr.found && mr.rank_absolute && mr.total_checked) {
    chips.push({ id: "maps", args: [mr.rank_absolute, mr.total_checked] });
  }

  // The regional median is already stated in the hero, so no chip repeats it.
  return chips.slice(0, 4);
}

// The paid report's contents as tiles with real counts. Names of directories,
// platforms, legal titles and PageSpeed values are never exposed here.
// This is a fixed, always-shown list of what the paid report contains — a
// sales teaser, not a live measurement dashboard. Every business sees the
// same 8 tiles so the report's scope reads consistently; when THIS
// business's data doesn't (yet) support a personalized number, the tile
// falls back to a generic description of the check instead of disappearing
// (owner correction, 2026-10-01: a missing/errored/zero-value measurement is
// not a reason to hide what the paid report covers).
export function lockedTiles(data) {
  const d = data || {};
  const tiles = [];

  const cit = d.citations;
  const citOk = cit && !cit.error && isNum(cit.total_count) && cit.total_count > 0 && isNum(cit.found_count);
  tiles.push({
    id: "directories",
    args: citOk ? [cit.found_count, cit.total_count, cit.total_count - cit.found_count] : [null, null, null],
  });

  const nap = d.nap_check;
  const napOk = nap && isNum(nap.checked) && nap.checked > 0 && isNum(nap.consistent);
  tiles.push({ id: "nap", args: napOk ? [nap.checked - nap.consistent, nap.checked] : [null, null] });

  // The paid report always runs the 12-question AI test.
  tiles.push({ id: "ai", args: [] });

  // The paid report always includes a manual deep-review of the Google
  // Business Profile (category, attributes, photos, …) — same "always
  // applies" pattern as the AI test above, and independent of the website.
  tiles.push({ id: "gbp", args: [] });

  const hasWebsite = d.free_facts?.has_website !== false;
  tiles.push({ id: "speed", args: [hasWebsite] });

  // The yellow legal card above (when present) only carries a count and the
  // "keine Rechtsberatung" disclaimer — it never says the check also covers
  // the photos used on the site. This tile spells out both halves of what
  // the paid legal check actually covers: website compliance and a
  // stock-photo copyright-risk scan.
  tiles.push({ id: "legal", args: [hasWebsite] });

  tiles.push({ id: "reviews", args: [isNum(d.review_count) ? d.review_count : null] });

  tiles.push({ id: "keywords", args: [] });

  return tiles;
}

// Fill-ins for "Drei Dinge" when pickTopHooks finds fewer than three. Only
// facts the reader can verify; order = priority.
export function fallbackHooks(data, need) {
  if (!isNum(need) || need <= 0) return [];
  const d = data || {};
  const hooks = [];

  const weakest = pillarOverview(d).filter((p) => isNum(d.score_pillars[p.key]));
  if (weakest.length) {
    // Lowest percentage, not lowest raw score (pillars have different maxima);
    // ties keep PILLAR_MAX order.
    const w = weakest.reduce((a, b) => (b.pct < a.pct ? b : a));
    // A pillar at 100% is not a weakness.
    if (w.pct < 100) hooks.push({ id: "weakestPillar", args: [w.key, w.score, w.max], checkArgs: [] });
    // The best area, only when it is a different pillar than the weakest.
    const s = weakest.reduce((a, b) => (b.pct > a.pct ? b : a));
    if (s.pct > 0 && s.key !== w.key) hooks.push({ id: "strongestPillar", args: [s.key, s.score, s.max], checkArgs: [] });
  }

  const revMedian = d.benchmark?.review_median;
  if (isNum(d.review_count) && isNum(revMedian)) {
    // Deliberately both directions: above the median is good news, and the
    // copy decides the wording.
    hooks.push({ id: "reviewsVsMedian", args: [d.review_count, revMedian], checkArgs: [] });
  }

  return hooks.slice(0, Math.floor(need));
}

// Points of the heatmap that carry a rank vs. all measured points. Null when
// there is no usable grid (missing, errored, empty).
export function gridStats(grid) {
  if (!grid || grid.error || !Array.isArray(grid.points) || !grid.points.length) return null;
  const total = grid.points.length;
  const visible = grid.points.filter((p) => p && isNum(p.rank)).length;
  return { visible, total };
}

// The hero headline, chosen from the data instead of the score band alone: a
// business that ranks top-3 on Google Maps must never be told "findet Sie kaum".
// Returns a descriptor {id, args}; id "band" means "use the score-band line".
// The "kaum" wording (band critical) therefore survives only when the Maps rank
// is missing / not found / worse than 10 — everything else that would
// contradict a real Maps rank is replaced by a truthful line.
export function heroHeadline(data) {
  const d = data || {};
  const band = verdictKey(d.digital_score);
  const fallback = { id: "band", args: [band] };
  // Strong/top scores already read positively; nothing to correct.
  if (band === "strong" || band === "top") return fallback;
  const mr = d.maps_rank;
  const rank = mr && !mr.error && mr.found !== false && isNum(mr.rank_absolute) && mr.rank_absolute > 0 ? mr.rank_absolute : null;
  if (rank != null && rank <= 3) {
    const g = gridStats(d.grid_rank);
    if (g && g.visible > 0) {
      return g.visible >= g.total
        ? { id: "mapsTopGridAll", args: [rank, g.total] }
        : { id: "mapsTopGrid", args: [rank, g.visible, g.total] };
    }
    return { id: "mapsTop", args: [rank] };
  }
  if (rank != null && rank <= 10) {
    return { id: "mapsMid", args: [rank, isNum(mr.total_checked) && mr.total_checked >= rank ? mr.total_checked : null] };
  }
  // No usable Maps rank for the primary keyword (missing / not found / worse
  // than 10): the heatmap is a second, independent measurement that can still
  // give a concrete, real gap to name instead of the generic band line — same
  // "only ever a real number" rule as the branches above.
  const g = gridStats(d.grid_rank);
  if (g && g.visible < g.total) {
    // The keyword ties the sentence to what was actually searched for THIS
    // business — without it, two businesses in the same situation read the
    // exact same sentence, which the numbers alone don't fix (owner
    // feedback, 2026-09-30: "sanki herkeste aynı yazı çıkıyor").
    const kw = typeof d.grid_rank?.keyword === "string" && d.grid_rank.keyword.trim() ? d.grid_rank.keyword.trim() : null;
    return { id: "gridWeak", args: [g.total - g.visible, g.total, kw] };
  }
  return fallback;
}

// "Was schon gut läuft": only what the free data proves. Descriptors again;
// the copy file words them. Empty when nothing qualifies.
export function strengths(data) {
  const d = data || {};
  const out = [];
  if (isNum(d.rating) && d.rating >= 4.5 && isNum(d.review_count) && d.review_count > 0) {
    out.push({ id: "rating", args: [d.rating, d.review_count] });
  }
  const mr = d.maps_rank;
  if (mr && !mr.error && mr.found !== false && isNum(mr.rank_absolute) && mr.rank_absolute >= 1 && mr.rank_absolute <= 3 && isNum(mr.total_checked)) {
    out.push({ id: "maps", args: [mr.rank_absolute, mr.total_checked] });
  }
  const pillars = d.score_pillars;
  if (pillars && typeof pillars === "object") {
    Object.keys(PILLAR_MAX).forEach((key) => {
      if (isNum(pillars[key]) && pillars[key] >= PILLAR_MAX[key]) {
        out.push({ id: "pillarFull", args: [key, pillars[key], PILLAR_MAX[key]] });
      }
    });
  }
  return out;
}

// No-website consolidation. The signal is the `no_website` finding in the free
// teaser or free_facts.has_website === false. Only pillars that are ACTUALLY 0
// count, so the "costs you N points" figure is never estimated.
const SITE_PILLARS = ["web", "onpage", "local"];
export function noWebsiteInfo(data) {
  const d = data || {};
  const teaser = Array.isArray(d.audit_findings_teaser) ? d.audit_findings_teaser : [];
  const inTeaser = teaser.some((f) => f && f.code === "no_website");
  if (!inTeaser && d.free_facts?.has_website !== false) return null;
  const pillars = d.score_pillars && typeof d.score_pillars === "object" ? d.score_pillars : {};
  const zeroed = SITE_PILLARS.filter((k) => isNum(pillars[k]) && pillars[k] === 0);
  const lost = zeroed.reduce((sum, k) => sum + PILLAR_MAX[k], 0);
  return { zeroed, lost, inTeaser };
}

// Up to three facts about the strongest competitor (top3[0]) vs. the business,
// built ONLY from the per-pillar scores the free payload carries for both.
// Two biggest leads of the competitor plus, when it exists, the biggest lead of
// the business; without an own lead the third slot is another competitor lead.
export function competitorFacts(data) {
  const d = data || {};
  const top1 = Array.isArray(d.top3) ? d.top3[0] : null;
  const own = d.score_pillars;
  if (!top1 || !top1.label || !top1.pillars || !own) return [];
  const rows = Object.keys(PILLAR_MAX)
    .filter((k) => isNum(top1.pillars[k]) && isNum(own[k]))
    .map((k) => ({ key: k, diff: top1.pillars[k] - own[k], theirs: top1.pillars[k], yours: own[k], max: PILLAR_MAX[k] }));
  const ahead = rows.filter((r) => r.diff > 0).sort((a, b) => b.diff - a.diff);
  const behind = rows.filter((r) => r.diff < 0).sort((a, b) => a.diff - b.diff);
  const picked = [
    ...ahead.slice(0, behind.length ? 2 : 3).map((r) => ({ id: "ahead", r })),
    ...behind.slice(0, 1).map((r) => ({ id: "behind", r })),
  ];
  return picked.map(({ id, r }) => ({ id, args: [r.key, top1.label, r.theirs, r.yours, r.max] }));
}

// "Die wichtigsten Baustellen": the n most severe findings of the free teaser
// (titles and the generic why sentence only — never evidence or fix).
const SEVERITY_RANK = { critical: 0, high: 1, medium: 2, low: 3 };
export function topIssues(data, n = 3) {
  const teaser = Array.isArray(data?.audit_findings_teaser) ? data.audit_findings_teaser : [];
  return teaser
    .filter((f) => f && f.title && f.severity in SEVERITY_RANK)
    .map((f, i) => ({ f, i }))
    .sort((a, b) => SEVERITY_RANK[a.f.severity] - SEVERITY_RANK[b.f.severity] || a.i - b.i)
    .slice(0, n)
    .map(({ f }) => ({ code: f.code, title: f.title, severity: f.severity, why: f.why || "" }));
}
